// Display filters only. Server current-staff policy remains the authority;
// neither a supported-book match nor a visible record grants a write.
import {linkedCoverageMembers,coverageMemberOptions} from '../../lib/schoolTeamOverlay.js'
const normalized = value => String(value ?? '').trim().toLowerCase()
export function inSupportedBook(record, actor) {
  if (actor?.id && linkedCoverageMembers(record).some(m => String(m.id) === String(actor.id) && m.role === 'support')) return true
  const names = new Set([actor?.id, actor?.name].map(normalized).filter(Boolean))
  if (!names.size) return false
  const raw = record?.raw || record || {}
  return [record?.supportRep, record?.supportRep2, raw.sr_support_rep_1, raw.sr_support_rep_2]
    .some(value => names.has(normalized(value)))
}

export function championshipReadNote({ count, loading, loadError, scope, complete }) {
  if (loading) return { kind: 'loading', headline: null, detail: null }
  if (loadError) return { kind: 'error', headline: 'Championship read incomplete',
    detail: `${loadError} Loaded records are not a complete championship count.` }
  if (!complete || count == null) return { kind: 'error', headline: 'Championship count not confirmed',
    detail: 'The complete championship read has not finished. This is not a count of zero.' }
  if (scope?.mode === 'all' || scope?.kind === 'company' || scope?.kind === 'team') return {
    kind: 'none', headline: 'Team championship visibility',
    detail: `${count.toLocaleString()} records loaded before display filters. Filter by assigned rep or recorded support assignments. Read visibility does not grant assignment, artwork, or financial access.`,
  }
  if (scope) return { kind: 'scoped', headline: 'Championship read is scoped',
    detail: `The server returned ${count.toLocaleString()} records in ${scope.rep || scope.repId || scope.mode || scope.kind || 'its declared scope'}. This is not a team-wide total.` }
  return { kind: 'none', headline: 'Championship scope not reported',
    detail: `${count.toLocaleString()} records loaded. Team-wide visibility has not been confirmed by the server.` }
}

// Do not reuse the TAM finance/rollup entitlement for a championship picker.
export function championshipSwitcherScope(read) {
  if (read?.status === 'loading') return {status:'loading',scope:null,error:null}
  if (read?.error || read?.complete !== true) return {status:'error',scope:null,error:read?.error || 'The complete championship read is unavailable.'}
  const all = read?.readScope?.mode === 'all'
  const reps = [...new Set((read?.data || []).flatMap(row => [row.rep, row.owner, row.supportRep, row.supportRep2]).filter(Boolean))].sort()
  return {status:'done',error:null,scope:{allowed:true,scope:all?'all':'own',reps:all?reps:[],coverageMembers:all?coverageMemberOptions(read.data || []):[],
    reason:all?'Team championship read only; action and financial permissions are unchanged.':'The server has not confirmed team-wide championship visibility.'}}
}

const SUPPORT_FIELDS = ['sr_support_rep_1', 'sr_support_rep_2']
export function supportedBookReadStatus({data = [], readEvidence, complete, status} = {}) {
  if (status === 'loading' || !complete) return {available:false, error:null}
  const unreadable = readEvidence?.unreadable || []
  const missing = data.some(row => {
    const raw = row?.raw || row || {}
    return SUPPORT_FIELDS.some(key => (!Object.hasOwn(raw,key) && !Object.hasOwn(raw.properties || {},key))
      || (raw._unreadable || []).includes(key))
  })
  const unavailable = readEvidence?.healthy === false || SUPPORT_FIELDS.some(key=>unreadable.includes(key)) || missing
  return {available:!unavailable,error:unavailable
    ? 'Supported-book membership is unavailable: current support-seat fields were missing or unreadable. No zero-book claim can be made. Choose an assigned rep to read that book; support seats do not grant working rights.' : null}
}

// Support membership cannot invalidate a complete whole-team championship view.
// Named worklists include support seats, so their membership still fails closed.
export function worklistReadFailure({sourceError, supportError, person} = {}) {
  return sourceError || (person === '*' ? null : supportError) || null
}

// Compact qualifier replaces, rather than stacks above, the existing search-count row.
export function championshipReadSummary(scope, count) {
  const label = scope?.mode === 'all' ? 'team championships' : scope ? `scope: ${scope.rep || scope.mode || scope.kind || 'server-scoped'}` : 'scope unconfirmed'
  return `${Number(count).toLocaleString()} loaded · ${label} · read-only`
}
